import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  productGroup: ProductGroups;
}

const CoPilotIntroduction: React.FC<Props> = ({ productGroup }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const hrBoosterLogo = '/dashboard/images/logos/hr_booster_logo_short.svg';
  const leadBoosterLogo = '/dashboard/images/logos/lead_booster_logo_short.svg';

  const logo = productGroup === ProductGroups.HR_BOOSTER ? hrBoosterLogo : leadBoosterLogo;

  return (
    <Stack direction={'row'} alignItems={'center'} sx={{ height: '100%' }}>
      <div className={classes.leftContent}>
        <div>
          <img src={logo} alt="logo" className={classes.logo} />
          <Stack gap={1}>
            <Typography className={classes.greyText}>{t('readyTo')}</Typography>
            <Typography className={classes.blueText} fontWeight={'bold'} mb={'8px'}>
              {getLocalizedText('automateLinkedinOutreach', productGroup, t)}
            </Typography>
            <Typography className={classes.greyText}>
              {getLocalizedText('aiPoweredOutreach', productGroup, t)}
            </Typography>
          </Stack>
        </div>
      </div>
      <div className={classes.rightContent}>
        <Stack gap={8}>
          <Typography className={classes.regularText} fontWeight={'bold'} sx={{ margin: '0 80px' }}>
            {t('whatToExpect')}
          </Typography>
          <Stack gap={1}>
            <Stack className={cx(classes.sectionIndicator, 'left')} direction="row" alignItems="center" gap={2}>
              <Stack direction="row" alignItems="center">
                <div className={classes.line} />
                <RadioButtonCheckedIcon className={classes.icon} />
              </Stack>
              <RocketLaunchIcon className={classes.bigIcon} />
              <Typography className={classes.regularBlueText} fontWeight={'bold'}>
                {t('youThePilot')}
              </Typography>
            </Stack>
            <Typography className={classes.subText} sx={{ margin: '0 80px', textAlign: 'left' }}>
              {t('defineWork')}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <Stack className={cx(classes.sectionIndicator, 'right')} direction="row" alignItems="center" gap={2}>
              <Typography className={classes.regularBlueText} fontWeight={'bold'}>
                {t('aiTheCoPilot')}
              </Typography>
              <SmartToyIcon className={classes.bigIcon} />
              <Stack direction="row" alignItems="center">
                <RadioButtonCheckedIcon className={classes.icon} />
                <div className={classes.line} />
              </Stack>
            </Stack>
            <Typography className={classes.subText} sx={{ margin: '0 80px', textAlign: 'right' }}>
              {t('aiDoesHeavyLifting')}
            </Typography>
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexBasis: '55%',
    height: '100%',
    padding: '24px clamp(24px, 5vw, 200px)',
  },
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexBasis: '45%',
    backgroundColor: theme.palette.backgrounds.main_new,
    borderRadius: '16px',
    height: '100%',
    padding: '80px 0',
  },
  logo: {
    width: '80px',
    height: '80px',
    marginBottom: '16px',
  },
  subText: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  greyText: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  blueText: {
    fontSize: theme.typography.pxToRem(45),
    color: theme.palette.primary.main,
    lineHeight: 1.2,
  },
  regularBlueText: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
    lineHeight: 1.2,
  },
  regularText: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  sectionIndicator: {
    '&.left': {
      justifyContent: 'flex-start',
    },
    '&.right': {
      justifyContent: 'flex-end',
    },
  },
  line: {
    background: theme.palette.primary.main,
    height: '2px',
    width: '80px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.primary.main,
  },
  sectionTitleText: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginLeft: '16px',
  },
  bigIcon: {
    fontSize: theme.typography.pxToRem(48),
    color: theme.palette.greys.medium,
  },
}));

export default CoPilotIntroduction;
